import React from 'react';
import { 
  Box, 
  Typography,
  AppBar,
  Toolbar,
  Divider,
  Tooltip,
  useTheme as useMuiTheme,
} from '@mui/material';
import SchedulerStatusIndicator from '../../shared/components/SchedulerStatusIndicator';
import { addThemeToUrl } from '../../context/ThemeContext';
import { useTheme } from '../../context/ThemeContext';
import Disclaimer from '../shared/Disclaimer';

const Header = ({ availableViews, selectedViewCode, organizationBranding = null }) => {
  const { currentTheme } = useTheme();
  const muiTheme = useMuiTheme();
  
  // Find the selected view from available views
  const selectedView = availableViews?.find(view => view.iso3 === selectedViewCode);
  
  // Organization branding configuration
  // Use logo-white.svg as default - the actual Situation Room logo in white
  const isUsingDefaultLogo = !organizationBranding?.logo;
  const logoSrc = organizationBranding?.logo || "/logo-white.svg";
  const logoStyle = organizationBranding?.logoStyle || 'normal';

  const title = organizationBranding?.headerTitle || organizationBranding?.title || "Situation Room AI";
  const subtitle = organizationBranding?.viewTitle ? 
    `${organizationBranding.viewTitle} Dashboard` : 
    (organizationBranding?.subtitle || "Humanitarian Dashboard");
  
  // Get logo filter based on style
  const getLogoFilter = () => {
    // Default logo (logo-white.svg) is already white, no filter needed
    if (isUsingDefaultLogo) {
      return 'none';
    }
    
    switch (logoStyle) {
      case 'invert':
        return 'brightness(0) invert(1)';
      case 'white':
        return 'brightness(0) invert(1)';
      case 'greyscale':
        return 'grayscale(100%)';
      case 'flexible-width':
        return isMinimalHeader ? 'none' : 'brightness(0) invert(1)';
      case 'normal':
      default:
        return isMinimalHeader ? 'none' : 'brightness(0) invert(1)';
    }
  };
  
  // Get logo dimensions based on style
  const getLogoDimensions = () => {
    if (logoStyle === 'flexible-width') {
      return {
        width: { xs: 'auto', sm: 'auto' },
        maxWidth: { xs: '120px', sm: '180px' },
        height: { xs: '22px', sm: '28px' }
      };
    }
    return {
      width: { xs: '22px', sm: '28px' },
      height: { xs: '22px', sm: '28px' }
    };
  };
  
  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    // Header debug logs removed for cleaner output
  }
  
  // Determine if we're using the IFRC theme
  const isIfrcTheme = currentTheme === 'ifrc';
  
  // Determine header style - minimal header should have white background
  const isMinimalHeader = organizationBranding?.headerStyle === 'minimal';
  const isFullHeader = organizationBranding?.headerStyle === 'full';
  const headerColorMode = organizationBranding?.headerColorMode || 'primary';
  
  // Determine background color based on header style and color mode
  let headerBgColor;
  if (isMinimalHeader) {
    headerBgColor = 'white';
  } else if (isFullHeader) {
    // Use headerColorMode to choose between primary and secondary
    if (headerColorMode === 'secondary') {
      headerBgColor = 'var(--color-secondary)';
    } else {
      headerBgColor = 'var(--color-primary)';
    }
  } else {
    // Fallback for legacy themes
    headerBgColor = isIfrcTheme ? 'var(--color-secondary)' : 'var(--color-primary)';
  }
  
  const headerTextColor = isMinimalHeader ? 'var(--color-primary)' : 'var(--color-background-paper)';
  const headerTitleColor = isMinimalHeader ? 'var(--color-primary)' : 'var(--color-background-paper)';
  const headerSubtitleColor = isMinimalHeader ? 'black' : 'var(--color-background-paper)';
  const headerDividerColor = isMinimalHeader ? 'rgba(0, 95, 148, 0.12)' : 'rgba(255,255,255,0.12)';
  
  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      component="header"
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: headerBgColor,
        padding: 0,
        '& .MuiToolbar-root': {
          bgcolor: headerBgColor
        }
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: { xs: 48, sm: 52 },
          height: { xs: 48, sm: 52 },
          px: { xs: 1, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 3 },
          justifyContent: 'space-between',
          overflow: 'hidden'
        }}
      >
        <Box 
          component="a" 
          href={addThemeToUrl('/')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            textDecoration: 'none',
            pointerEvents: { xs: 'none', sm: 'auto' }, // Disable click on mobile
            '&:hover': {
              opacity: { xs: 1, sm: 0.9 } // No hover effect on mobile
            },
            '& img': {
              // Dynamic filter based on logo style - NONE for default logo
              filter: isUsingDefaultLogo ? 'none' : getLogoFilter(),
              ...getLogoDimensions(),
              marginRight: { xs: '8px', sm: '15px' },
              marginLeft: { xs: '-4px', sm: '-9px' },
              paddingLeft: '3px',
              objectFit: 'contain',
              background: 'transparent'
            }
          }}
        >
          <img 
            src={logoSrc} 
            alt={`${title} Logo`}
            data-logo-debug={`src:${logoSrc},default:${isUsingDefaultLogo},filter:${isUsingDefaultLogo ? 'none' : getLogoFilter()}`}
            onError={(e) => {
              // Avoid infinite error loops and noisy logs in production.
              const img = e.currentTarget;
              const fallbackStep = Number(img.dataset.fallbackStep || '0');
              if (fallbackStep >= 3) return;

              if (fallbackStep === 0 && process.env.NODE_ENV === 'development' && organizationBranding?.logo) {
                // console.warn('⚠️ Failed to load organization logo, applying fallback:', img.src);
              }

              img.dataset.fallbackStep = String(fallbackStep + 1);
              if (fallbackStep === 0) {
                // Try the white logo (for email compatibility)
                img.src = '/logo-white.svg';
              } else if (fallbackStep === 1) {
                // Try the fixed logo
                img.src = '/logo-200-fixed.png';
              } else {
                // Final fallback
                img.src = '/vite.svg';
              }
            }}
            onLoad={(e) => {
              void e;
            }}
          />
          <Box sx={{ 
            minWidth: 0, 
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            overflow: 'hidden'
          }}>
            <Typography 
              variant="h6" 
              component="div"
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                lineHeight: 'var(--line-height-tight)',
                letterSpacing: '0.02em',
                color: headerTitleColor,
                mb: { xs: 0.1, sm: 0.25 },
                fontFamily: organizationBranding?.fontFamily || 'var(--font-family-heading)',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="caption" 
              component="div"
              sx={{ 
                opacity: 0.85,
                fontSize: { xs: '0.65rem', sm: 'var(--font-size-md)' },
                letterSpacing: '0.01em',
                color: headerSubtitleColor,
                lineHeight: 'var(--line-height-tight)',
                fontFamily: organizationBranding?.fontFamily || 'var(--font-family-heading)',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Divider 
          orientation="vertical" 
          flexItem 
          sx={{ 
            borderColor: headerDividerColor,
            display: { xs: 'none', sm: 'block' } // Hide on mobile
          }}
        />
        
        {/* Scheduler Status Indicator - Hidden on mobile and controllable via settings */}
        {(organizationBranding?.showStatusSchedule === undefined || organizationBranding?.showStatusSchedule === true) && 
         organizationBranding?.statusIndicatorStyle !== 'hidden' && (
          <Box sx={{ 
            display: { xs: 'none', sm: 'block' },
            color: isMinimalHeader ? 'black' : 'var(--color-background-paper)',
            '& .MuiTypography-root': {
              color: isMinimalHeader ? 'black' : 'var(--color-background-paper)'
            }
          }}>
            <SchedulerStatusIndicator style={organizationBranding?.statusIndicatorStyle} />
          </Box>
        )}

        {/* Spacer to push disclaimer to right */}
        <Box sx={{ flex: 1 }} />

        {/* Disclaimer Badge - Moved to right side, hidden on small mobile, only show if enabled */}
        {(organizationBranding?.showDisclaimer !== false) && (
          <Box sx={{ 
            display: { xs: 'none', sm: 'block' },
            color: isMinimalHeader ? 'black' : 'var(--color-background-paper)',
            '& .MuiTypography-root': {
              color: isMinimalHeader ? 'black' : 'var(--color-background-paper)'
            }
          }}>
            <Disclaimer 
              selectedView={selectedView}
              variant="header"
              headerStyle={organizationBranding?.headerStyle}
              organizationBranding={organizationBranding}
            />
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default React.memo(Header, (prevProps, nextProps) => {
  // Custom comparison function since organizationBranding is an object
  return prevProps.selectedViewCode === nextProps.selectedViewCode &&
         JSON.stringify(prevProps.organizationBranding) === JSON.stringify(nextProps.organizationBranding);
});
